import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore, Button, Input, Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter, Badge } from '@dukene/shared';
import { User, Lock, CheckCircle2, AlertCircle } from 'lucide-react';

const profileSchema = z.object({
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    email: z.string().email('Invalid email address'),
});

const passwordSchema = z.object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z.string().min(8, 'New password must be at least 8 characters'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
}).refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
});

type ProfileFormData = z.infer<typeof profileSchema>;
type PasswordFormData = z.infer<typeof passwordSchema>;

export default function Profile() {
    const user = useAuthStore((state) => state.user);
    const updateProfile = useAuthStore((state) => state.updateProfile);
    const changePassword = useAuthStore((state) => state.changePassword);

    const [isProfileLoading, setIsProfileLoading] = useState(false);
    const [isPasswordLoading, setIsPasswordLoading] = useState(false);
    const [profileMessage, setProfileMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
    const [passwordMessage, setPasswordMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    const {
        register: registerProfile,
        handleSubmit: handleProfileSubmit,
        formState: { errors: profileErrors },
    } = useForm<ProfileFormData>({
        resolver: zodResolver(profileSchema),
        defaultValues: {
            firstName: user?.firstName || '',
            lastName: user?.lastName || '',
            email: user?.email || '',
        },
    });

    const {
        register: registerPassword,
        handleSubmit: handlePasswordSubmit,
        reset: resetPassword,
        formState: { errors: passwordErrors },
    } = useForm<PasswordFormData>({
        resolver: zodResolver(passwordSchema),
    });

    const onProfileSubmit = async (data: ProfileFormData) => {
        try {
            setIsProfileLoading(true);
            setProfileMessage(null);
            await updateProfile(data);
            setProfileMessage({ type: 'success', text: 'Profile updated successfully' });
        } catch (err: any) {
            setProfileMessage({ type: 'error', text: err.message });
        } finally {
            setIsProfileLoading(false);
        }
    };

    const onPasswordSubmit = async (data: PasswordFormData) => {
        try {
            setIsPasswordLoading(true);
            setPasswordMessage(null);
            await changePassword({
                currentPassword: data.currentPassword,
                newPassword: data.newPassword,
            });
            setPasswordMessage({ type: 'success', text: 'Password changed successfully' });
            resetPassword();
        } catch (err: any) {
            setPasswordMessage({ type: 'error', text: err.message });
        } finally {
            setIsPasswordLoading(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto space-y-8">
            <div>
                <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
                <p className="text-gray-500">Manage your personal information and security settings</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* Profile Card */}
                <div className="md:col-span-1">
                    <Card className="border-none shadow-sm bg-black text-white overflow-hidden">
                        <CardContent className="pt-8 pb-8 flex flex-col items-center text-center">
                            <div className="w-24 h-24 rounded-full bg-white/10 flex items-center justify-center mb-4 border-2 border-white/20">
                                <span className="text-3xl font-bold">
                                    {user?.firstName?.[0]}{user?.lastName?.[0]}
                                </span>
                            </div>
                            <h3 className="text-xl font-bold">{user?.firstName} {user?.lastName}</h3>
                            <p className="text-white/60 text-sm mb-4">{user?.email}</p>
                            <Badge variant="secondary" className="bg-white/10 text-white border-none">
                                {user?.role}
                            </Badge>
                        </CardContent>
                    </Card>
                </div>

                {/* Edit Forms */}
                <div className="md:col-span-2 space-y-8">
                    {/* General Information */}
                    <Card className="border-gray-200 shadow-sm">
                        <form onSubmit={handleProfileSubmit(onProfileSubmit)}>
                            <CardHeader>
                                <div className="flex items-center gap-2 mb-1">
                                    <User className="w-4 h-4 text-gray-400" />
                                    <CardTitle>General Information</CardTitle>
                                </div>
                                <CardDescription>Update your personal details here.</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {profileMessage && (
                                    <div className={`p-4 rounded-xl flex items-center gap-3 ${profileMessage.type === 'success' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'
                                        }`}>
                                        {profileMessage.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                                        <span className="text-sm font-medium">{profileMessage.text}</span>
                                    </div>
                                )}

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700">First Name</label>
                                        <Input
                                            {...registerProfile('firstName')}
                                            placeholder="Enter first name"
                                            className="rounded-xl border-gray-200 h-11"
                                        />
                                        {profileErrors.firstName && <p className="text-xs text-red-500">{profileErrors.firstName.message}</p>}
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700">Last Name</label>
                                        <Input
                                            {...registerProfile('lastName')}
                                            placeholder="Enter last name"
                                            className="rounded-xl border-gray-200 h-11"
                                        />
                                        {profileErrors.lastName && <p className="text-xs text-red-500">{profileErrors.lastName.message}</p>}
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-semibold text-gray-700">Email Address</label>
                                    <Input
                                        {...registerProfile('email')}
                                        type="email"
                                        placeholder="Enter email address"
                                        className="rounded-xl border-gray-200 h-11"
                                    />
                                    {profileErrors.email && <p className="text-xs text-red-500">{profileErrors.email.message}</p>}
                                </div>
                            </CardContent>
                            <CardFooter className="bg-gray-50/50 justify-end border-t border-gray-100 mt-4 py-4">
                                <Button
                                    type="submit"
                                    disabled={isProfileLoading}
                                    className="bg-black text-white hover:bg-gray-800 rounded-xl h-11 px-6 font-semibold"
                                >
                                    {isProfileLoading ? 'Saving...' : 'Save Changes'}
                                </Button>
                            </CardFooter>
                        </form>
                    </Card>

                    {/* Change Password */}
                    <Card className="border-gray-200 shadow-sm">
                        <form onSubmit={handlePasswordSubmit(onPasswordSubmit)}>
                            <CardHeader>
                                <div className="flex items-center gap-2 mb-1">
                                    <Lock className="w-4 h-4 text-gray-400" />
                                    <CardTitle>Change Password</CardTitle>
                                </div>
                                <CardDescription>Ensure your account is using a long, random password to stay secure.</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {passwordMessage && (
                                    <div className={`p-4 rounded-xl flex items-center gap-3 ${passwordMessage.type === 'success' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'
                                        }`}>
                                        {passwordMessage.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                                        <span className="text-sm font-medium">{passwordMessage.text}</span>
                                    </div>
                                )}

                                <div className="space-y-2">
                                    <label className="text-sm font-semibold text-gray-700">Current Password</label>
                                    <Input
                                        {...registerPassword('currentPassword')}
                                        type="password"
                                        placeholder="Enter current password"
                                        className="rounded-xl border-gray-200 h-11"
                                    />
                                    {passwordErrors.currentPassword && <p className="text-xs text-red-500">{passwordErrors.currentPassword.message}</p>}
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-semibold text-gray-700">New Password</label>
                                    <Input
                                        {...registerPassword('newPassword')}
                                        type="password"
                                        placeholder="Enter new password"
                                        className="rounded-xl border-gray-200 h-11"
                                    />
                                    {passwordErrors.newPassword && <p className="text-xs text-red-500">{passwordErrors.newPassword.message}</p>}
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-semibold text-gray-700">Confirm New Password</label>
                                    <Input
                                        {...registerPassword('confirmPassword')}
                                        type="password"
                                        placeholder="Confirm new password"
                                        className="rounded-xl border-gray-200 h-11"
                                    />
                                    {passwordErrors.confirmPassword && <p className="text-xs text-red-500">{passwordErrors.confirmPassword.message}</p>}
                                </div>
                            </CardContent>
                            <CardFooter className="bg-gray-50/50 justify-end border-t border-gray-100 mt-4 py-4">
                                <Button
                                    type="submit"
                                    disabled={isPasswordLoading}
                                    className="bg-black text-white hover:bg-gray-800 rounded-xl h-11 px-6 font-semibold"
                                >
                                    {isPasswordLoading ? 'Updating...' : 'Update Password'}
                                </Button>
                            </CardFooter>
                        </form>
                    </Card>
                </div>
            </div>
        </div>
    );
}
